import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiPlus, FiSearch, FiTrash2, FiX } from "react-icons/fi";
import bankService from "../../../services/bank.service";
import ExportExcelButton from "../../../components/common/ExportExcelButton";
import { downloadExcelRows } from "../../../utils/downloadExcel";
import "../../../styles/users.css";
import "../../../styles/ssdiLinkBanker.css";

function dash(value) {
  const text = String(value || "").trim();
  return text || "—";
}

function formatLinkedOn(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function formatRegistrationDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB");
}

function serialNumber(index, total) {
  return Math.max(Number(total) - index, 1);
}

const LINKED_AGENCY_COLUMNS = 22;

function statusClass(status) {
  const key = String(status || "").toLowerCase();
  if (key === "active" || key === "pending") return key;
  return "inactive";
}

function matchesQuery(haystack, query) {
  if (!query) return true;
  return String(haystack || "")
    .toLowerCase()
    .includes(query);
}

function SearchablePicker({
  id,
  label,
  query,
  onQueryChange,
  placeholder,
  options,
  value,
  onChange,
  emptyText,
}) {
  const selected = options.find((item) => item.id === value) || null;
  const q = String(query || "").trim().toLowerCase();
  const visible = q
    ? options.filter((item) => matchesQuery(item.searchText || item.title, q))
    : options;

  return (
    <div className="sla-picker">
      <label htmlFor={id}>{label}</label>
      {selected ? (
        <div className="sla-picker__chosen">
          <div className="sla-picker__chosen-text">
            <strong>{selected.title}</strong>
            {selected.subtitle ? <small>{selected.subtitle}</small> : null}
          </div>
          <button
            type="button"
            className="sla-picker__change"
            onClick={() => {
              onChange("");
              onQueryChange("");
            }}
          >
            Change
          </button>
        </div>
      ) : (
        <>
          <div className="sla-picker__search">
            <FiSearch aria-hidden />
            <input
              id={id}
              type="search"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder={placeholder}
              autoComplete="off"
            />
          </div>
          <ul className="sla-picker__list" role="listbox" aria-label={label}>
            {visible.length === 0 ? (
              <li className="sla-picker__empty">{emptyText}</li>
            ) : (
              visible.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className="sla-picker__option"
                    onClick={() => onChange(item.id)}
                  >
                    <strong>{item.title}</strong>
                    {item.subtitle ? <small>{item.subtitle}</small> : null}
                  </button>
                </li>
              ))
            )}
          </ul>
        </>
      )}
    </div>
  );
}

function LinkedByCell({ linkedBy }) {
  if (!linkedBy) return "—";
  return (
    <div className="sla-linked-by">
      <strong>{dash(linkedBy.name)}</strong>
      <span>{dash(linkedBy.employeeNumber)}</span>
      <span>{dash(linkedBy.email)}</span>
      <span>{dash(linkedBy.mobile)}</span>
    </div>
  );
}

function AgencyUsers({ agency }) {
  return (
    <div className="sla-users">
      <strong>{Number(agency.totalUsers) || 0}</strong>
      <small>
        Active {Number(agency.activeUsers) || 0} · Inactive {Number(agency.inactiveUsers) || 0}
      </small>
    </div>
  );
}

function AgencyActions({ agency, removingId, onUnlink }) {
  return (
    <div className="sla-row-actions">
      <button
        type="button"
        className="company-btn company-btn--delete"
        disabled={removingId === agency.linkId}
        onClick={() => onUnlink(agency)}
      >
        <FiTrash2 aria-hidden />
        <span>{removingId === agency.linkId ? "Unlinking…" : "Unlink"}</span>
      </button>
    </div>
  );
}

export default function SsdiLinkBankerAgency({ bank }) {
  const bankId = bank?._id || bank?.id || "";
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [removingId, setRemovingId] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [employeeNumber, setEmployeeNumber] = useState("");
  const [searching, setSearching] = useState(false);
  const [lookup, setLookup] = useState(null);
  const [searchError, setSearchError] = useState("");
  const [available, setAvailable] = useState([]);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [agencyQuery, setAgencyQuery] = useState("");
  const [selectedAgencyId, setSelectedAgencyId] = useState("");
  const [linkSaving, setLinkSaving] = useState(false);
  const [linkError, setLinkError] = useState("");
  const searchLock = useRef(false);
  const createLock = useRef(false);

  const loadAgencies = useCallback(async () => {
    if (!bankId) return;
    const res = await bankService.ssdiLinkedAgencies(bankId);
    setAgencies(res?.data?.data?.linkedAgencies || []);
  }, [bankId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    loadAgencies()
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.message || "Failed to load linked agencies.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadAgencies]);

  useEffect(() => {
    if (!createOpen) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape" && !linkSaving) setCreateOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [createOpen, linkSaving]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return agencies;
    return agencies.filter((agency) =>
      [
        agency.name,
        agency.code,
        agency.adminName,
        agency.adminEmail,
        agency.companyEmail,
        agency.phone,
        agency.firstConfirmationNumber,
        agency.secondConfirmationNumber,
        agency.panNumber,
        agency.gstNumber,
        agency.aadhaarNumber,
        agency.address || agency.location,
        agency.pincode,
        agency.status,
        agency.linkedBy?.name,
        agency.linkedBy?.employeeNumber,
        agency.linkedBy?.email,
        agency.linkedBy?.mobile,
      ].some((field) => matchesQuery(field, q))
    );
  }, [agencies, search]);

  const resetLookup = () => {
    setEmployeeNumber("");
    setLookup(null);
    setSearchError("");
    setLinkError("");
    setSearching(false);
    setAvailable([]);
    setAgencyQuery("");
    setSelectedAgencyId("");
    setAvailableLoading(false);
  };

  useEffect(() => {
    resetLookup();
    setCreateOpen(false);
  }, [bankId]);

  const agencyOptions = useMemo(
    () =>
      available.map((agency) => ({
        id: String(agency.id),
        title: agency.name || "Agency",
        subtitle: [agency.code, agency.adminName, agency.adminEmail].filter(Boolean).join(" — "),
        searchText: [agency.name, agency.code, agency.adminName, agency.adminEmail].join(" "),
      })),
    [available]
  );

  const openCreate = async () => {
    resetLookup();
    setNotice("");
    setCreateOpen(true);
    setAvailableLoading(true);
    try {
      const res = await bankService.ssdiAvailableAgencies(bankId);
      setAvailable(res?.data?.data?.agencies || []);
    } catch (err) {
      setLinkError(err?.response?.data?.message || "Failed to load agencies.");
      setAvailable([]);
    } finally {
      setAvailableLoading(false);
    }
  };

  const searchEmployee = async () => {
    if (searchLock.current || linkSaving) return;
    const number = employeeNumber.trim();
    setLookup(null);
    setLinkError("");
    setSearchError("");
    if (!number) {
      setSearchError("Employee number is required.");
      return;
    }
    searchLock.current = true;
    setSearching(true);
    try {
      const res = await bankService.ssdiFindEmployeeForLink(bankId, number);
      const data = res?.data?.data;
      if (!data?.employee?.id) {
        setSearchError("Employee not found for this bank / NBFC.");
        return;
      }
      setLookup(data);
    } catch (err) {
      setSearchError(
        err?.response?.data?.message || "Employee not found for this bank / NBFC."
      );
    } finally {
      searchLock.current = false;
      setSearching(false);
    }
  };

  const handleCreateLink = async (event) => {
    event.preventDefault();
    if (createLock.current || linkSaving) return;
    setLinkError("");
    setNotice("");
    if (!lookup?.employee?.id) {
      setLinkError("Search a valid employee before creating the link.");
      return;
    }
    if (!selectedAgencyId) {
      setLinkError("Select an agency.");
      return;
    }
    createLock.current = true;
    setLinkSaving(true);
    try {
      const res = await bankService.ssdiCreateLink({
        bankId: String(bankId),
        agencyId: selectedAgencyId,
        employeeNumber: lookup.employee.employeeNumber,
        linkedByUserId: String(lookup.employee.id),
      });
      setCreateOpen(false);
      resetLookup();
      await loadAgencies();
      setNotice(
        res?.data?.message || `Agency linked successfully with ${bank?.bankName || "this bank / NBFC"}.`
      );
    } catch (err) {
      setLinkError(err?.response?.data?.message || "Failed to create link.");
    } finally {
      createLock.current = false;
      setLinkSaving(false);
    }
  };

  const handleUnlink = async (agency) => {
    const label = agency.name || "this agency";
    if (!window.confirm(`Unlink ${label} from ${bank?.bankName || "this bank / NBFC"}?`)) return;
    setRemovingId(agency.linkId);
    setError("");
    try {
      await bankService.ssdiDeleteLink(agency.linkId);
      await loadAgencies();
      setNotice(`${label} unlinked.`);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to unlink agency.");
    } finally {
      setRemovingId("");
    }
  };

  const countLabel = `${agencies.length} ${agencies.length === 1 ? "Agency" : "Agencies"} Linked`;

  const downloadAgencies = () => {
    const headers = [
      "S.No.",
      "Agency Code",
      "Agency Name",
      "Admin Name",
      "First Confirmation Number",
      "Second Confirmation Number",
      "Admin Email",
      "Repo Agency Email",
      "PAN Number",
      "GST Number",
      "Aadhaar Number",
      "Address",
      "Pincode",
      "Registration Date",
      "Agency Status",
      "Users",
      "Linked By Bank / NBFC Employee Name",
      "Linked By Employee Number",
      "Linked By Email",
      "Linked By Mobile",
      "Linked On",
    ];
    const rows = agencies.map((agency, index) => ({
      "S.No.": serialNumber(index, agencies.length),
      "Agency Code": agency.code || "",
      "Agency Name": agency.name || "",
      "Admin Name": agency.adminName || "",
      "First Confirmation Number": agency.firstConfirmationNumber || "",
      "Second Confirmation Number": agency.secondConfirmationNumber || "",
      "Admin Email": agency.adminEmail || "",
      "Repo Agency Email": agency.companyEmail || "",
      "PAN Number": agency.panNumber || "",
      "GST Number": agency.gstNumber || "",
      "Aadhaar Number": agency.aadhaarNumber || "",
      Address: agency.address || agency.location || "",
      Pincode: agency.pincode || "",
      "Registration Date":
        formatRegistrationDate(agency.registrationDate) === "—"
          ? ""
          : formatRegistrationDate(agency.registrationDate),
      "Agency Status": agency.status || "",
      Users: Number(agency.totalUsers) || 0,
      "Linked By Bank / NBFC Employee Name": agency.linkedBy?.name || "",
      "Linked By Employee Number": agency.linkedBy?.employeeNumber || "",
      "Linked By Email": agency.linkedBy?.email || "",
      "Linked By Mobile": agency.linkedBy?.mobile || "",
      "Linked On": formatLinkedOn(agency.linkedAt) === "—" ? "" : formatLinkedOn(agency.linkedAt),
    }));
    const code = String(bank?.bankCode || bankId || "bank").replace(/[^\w.-]+/g, "");
    downloadExcelRows(rows, `linked-agencies-${code}.xlsx`, headers);
  };

  return (
    <section
      className={`create-company-card ssdi-bank-details-card sla-card${
        agencies.length > 0 ? " sla-card--filled" : ""
      }`}
    >
      <header className="sla-head">
        <div>
          <h3>Linked Agencies</h3>
          <p className="sla-stats">{countLabel}</p>
        </div>
        <div className="sla-head-actions">
          <ExportExcelButton onClick={downloadAgencies} label="Download Excel" />
          <button type="button" className="primary-page-btn" onClick={openCreate}>
            <FiPlus aria-hidden />
            Link Agency
          </button>
        </div>
      </header>

      {agencies.length > 0 ? (
        <div className="company-search-panel company-search-panel--simple sla-search">
          <div className="company-search-bar">
            <FiSearch className="company-search-icon" aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search agency, code, admin, email, or phone…"
              aria-label="Search linked agencies"
            />
          </div>
        </div>
      ) : null}

      {notice ? <p className="sla-notice">{notice}</p> : null}
      {error ? <p className="error-text">{error}</p> : null}

      <div className="company-table-wrap ssdi-excel-sheet table-scroll sla-table-wrap">
        <table className="users-table excel-grid-table sla-table">
          <thead>
            <tr>
              <th>S.No.</th>
              <th>Agency Code</th>
              <th>Agency Name</th>
              <th>Admin Name</th>
              <th>First Confirmation Number</th>
              <th>Second Confirmation Number</th>
              <th>Admin Email</th>
              <th>Repo Agency Email</th>
              <th>PAN Number</th>
              <th>GST Number</th>
              <th>Aadhaar Number</th>
              <th>Address</th>
              <th>Pincode</th>
              <th>Registration Date</th>
              <th>Agency Status</th>
              <th>Users</th>
              <th>Linked By Bank / NBFC Employee Name</th>
              <th>Linked By Employee Number</th>
              <th>Linked By Email</th>
              <th>Linked By Mobile</th>
              <th>Linked On</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={LINKED_AGENCY_COLUMNS}>Loading linked agencies…</td>
              </tr>
            ) : agencies.length === 0 ? (
              <tr>
                <td colSpan={LINKED_AGENCY_COLUMNS}>No agencies linked with this bank / NBFC yet.</td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={LINKED_AGENCY_COLUMNS}>No linked agencies match your search.</td>
              </tr>
            ) : (
              filtered.map((agency) => {
                    const fullIndex = agencies.findIndex(
                      (row) => (row.linkId || row.id) === (agency.linkId || agency.id)
                    );
                    return (
                    <tr key={agency.linkId || agency.id}>
                      <td>{serialNumber(fullIndex < 0 ? 0 : fullIndex, agencies.length)}</td>
                      <td>{dash(agency.code)}</td>
                      <td>{dash(agency.name)}</td>
                      <td>{dash(agency.adminName)}</td>
                      <td className="sla-nowrap">{dash(agency.firstConfirmationNumber)}</td>
                      <td className="sla-nowrap">{dash(agency.secondConfirmationNumber)}</td>
                      <td>{dash(agency.adminEmail)}</td>
                      <td>{dash(agency.companyEmail)}</td>
                      <td className="sla-nowrap">{dash(agency.panNumber)}</td>
                      <td className="sla-nowrap">{dash(agency.gstNumber)}</td>
                      <td className="sla-nowrap">{dash(agency.aadhaarNumber)}</td>
                      <td className="sla-address-td">
                        <span className="sla-address" title={agency.address || agency.location || ""}>
                          {dash(agency.address || agency.location)}
                        </span>
                      </td>
                      <td>{dash(agency.pincode)}</td>
                      <td className="sla-nowrap">{formatRegistrationDate(agency.registrationDate)}</td>
                      <td>
                        <span className={`company-status company-status--${statusClass(agency.status)}`}>
                          {dash(agency.status)}
                        </span>
                      </td>
                      <td>
                        <AgencyUsers agency={agency} />
                      </td>
                      <td>{dash(agency.linkedBy?.name)}</td>
                      <td className="sla-nowrap">{dash(agency.linkedBy?.employeeNumber)}</td>
                      <td>{dash(agency.linkedBy?.email)}</td>
                      <td className="sla-nowrap">{dash(agency.linkedBy?.mobile)}</td>
                      <td>{formatLinkedOn(agency.linkedAt)}</td>
                      <td>
                        <AgencyActions
                          agency={agency}
                          removingId={removingId}
                          onUnlink={handleUnlink}
                        />
                      </td>
                    </tr>
                    );
              })
            )}
          </tbody>
        </table>
      </div>

      {agencies.length > 0 ? (
        <div className="sla-cards">
          {filtered.length === 0 ? (
            <p className="muted">No linked agencies match your search.</p>
          ) : (
            filtered.map((agency) => (
                <article key={`card-${agency.linkId || agency.id}`} className="sla-agency-card">
                  <header>
                    <h4>{dash(agency.name)}</h4>
                    <span className={`company-status company-status--${statusClass(agency.status)}`}>
                      {dash(agency.status)}
                    </span>
                  </header>
                  <dl>
                    <div>
                      <dt>Agency code</dt>
                      <dd>{dash(agency.code)}</dd>
                    </div>
                    <div>
                      <dt>Admin name</dt>
                      <dd>{dash(agency.adminName)}</dd>
                    </div>
                    <div>
                      <dt>First confirmation number</dt>
                      <dd>{dash(agency.firstConfirmationNumber)}</dd>
                    </div>
                    <div>
                      <dt>Second confirmation number</dt>
                      <dd>{dash(agency.secondConfirmationNumber)}</dd>
                    </div>
                    <div>
                      <dt>Admin email</dt>
                      <dd>{dash(agency.adminEmail)}</dd>
                    </div>
                    <div>
                      <dt>Repo agency email</dt>
                      <dd>{dash(agency.companyEmail)}</dd>
                    </div>
                    <div>
                      <dt>PAN number</dt>
                      <dd>{dash(agency.panNumber)}</dd>
                    </div>
                    <div>
                      <dt>GST number</dt>
                      <dd>{dash(agency.gstNumber)}</dd>
                    </div>
                    <div>
                      <dt>Aadhaar number</dt>
                      <dd>{dash(agency.aadhaarNumber)}</dd>
                    </div>
                    <div>
                      <dt>Address</dt>
                      <dd className="sla-address" title={agency.address || agency.location || ""}>
                        {dash(agency.address || agency.location)}
                      </dd>
                    </div>
                    <div>
                      <dt>Pincode</dt>
                      <dd>{dash(agency.pincode)}</dd>
                    </div>
                    <div>
                      <dt>Registration date</dt>
                      <dd>{formatRegistrationDate(agency.registrationDate)}</dd>
                    </div>
                    <div>
                      <dt>Users</dt>
                      <dd>
                        <AgencyUsers agency={agency} />
                      </dd>
                    </div>
                    <div>
                      <dt>Linked by bank / NBFC employee name</dt>
                      <dd>{dash(agency.linkedBy?.name)}</dd>
                    </div>
                    <div>
                      <dt>Linked by employee number</dt>
                      <dd>{dash(agency.linkedBy?.employeeNumber)}</dd>
                    </div>
                    <div>
                      <dt>Linked by email</dt>
                      <dd>{dash(agency.linkedBy?.email)}</dd>
                    </div>
                    <div>
                      <dt>Linked by mobile</dt>
                      <dd>{dash(agency.linkedBy?.mobile)}</dd>
                    </div>
                    <div>
                      <dt>Linked on</dt>
                      <dd>{formatLinkedOn(agency.linkedAt)}</dd>
                    </div>
                  </dl>
                  <AgencyActions agency={agency} removingId={removingId} onUnlink={handleUnlink} />
                </article>
            ))
          )}
        </div>
      ) : null}

      {createOpen ? (
        <div
          className="modal-overlay"
          role="presentation"
          onClick={() => !linkSaving && setCreateOpen(false)}
        >
          <div
            className="modal-box sla-modal sla-modal--create"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sla-create-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sla-modal__head">
              <div>
                <h3 id="sla-create-title">Create Link</h3>
                <p className="sla-modal__hint">
                  This bank / NBFC is already selected. Enter a bank / NBFC admin or bank / NBFC user employee number.
                  That person is saved as Linked By for the agency you choose.
                </p>
              </div>
              <button
                type="button"
                className="sla-close"
                onClick={() => !linkSaving && setCreateOpen(false)}
                aria-label="Close create link"
                disabled={linkSaving}
              >
                <FiX aria-hidden />
              </button>
            </div>
            <form className="sla-create-form" onSubmit={handleCreateLink}>
              <div className="sla-picker">
                <label id="sla-banker-label">Bank / NBFC</label>
                <div className="sla-banker" role="group" aria-labelledby="sla-banker-label">
                  <div className="sla-banker__text">
                    <strong>{dash(bank?.bankName)}</strong>
                    <small>
                      {[bank?.bankCode, bank?.email].filter(Boolean).join(" · ") || "—"}
                    </small>
                  </div>
                  <span className="sla-banker__badge">Selected</span>
                </div>
              </div>
              <div className="sla-picker">
                <label htmlFor="sla-employee-number">Employee Number *</label>
                <div className="sla-emp-search">
                  <input
                    id="sla-employee-number"
                    value={employeeNumber}
                    onChange={(e) => {
                      setEmployeeNumber(e.target.value);
                      setLookup(null);
                      setSearchError("");
                      setLinkError("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (!searching && !linkSaving) searchEmployee();
                      }
                    }}
                    placeholder="Enter Employee Number"
                    autoComplete="off"
                    disabled={linkSaving}
                    required
                  />
                  <button
                    type="button"
                    className="secondary-page-btn"
                    onClick={searchEmployee}
                    disabled={searching || linkSaving || !employeeNumber.trim()}
                  >
                    Search Employee
                  </button>
                </div>
                {searching ? <p className="muted">Searching employee...</p> : null}
                {searchError ? <p className="error-text">{searchError}</p> : null}
              </div>
              {lookup?.employee ? (
                <div className="sla-found">
                  <h4>Employee Found</h4>
                  <dl>
                    <div>
                      <dt>Name</dt>
                      <dd>{dash(lookup.employee.name)}</dd>
                    </div>
                    <div>
                      <dt>Employee Number</dt>
                      <dd>{dash(lookup.employee.employeeNumber)}</dd>
                    </div>
                    <div>
                      <dt>Email</dt>
                      <dd>{dash(lookup.employee.email)}</dd>
                    </div>
                    <div>
                      <dt>Mobile</dt>
                      <dd>{dash(lookup.employee.mobile)}</dd>
                    </div>
                  </dl>
                </div>
              ) : null}
              {lookup?.employee ? (
                availableLoading ? (
                  <p className="muted">Loading agencies…</p>
                ) : (
                  <SearchablePicker
                    id="sla-agency-search"
                    label="Agency / Repo Admin"
                    query={agencyQuery}
                    onQueryChange={setAgencyQuery}
                    placeholder="Search agency name, code, admin, or email"
                    options={agencyOptions}
                    value={selectedAgencyId}
                    onChange={setSelectedAgencyId}
                    emptyText={
                      available.length
                        ? "No agencies match your search."
                        : "No agencies available to link."
                    }
                  />
                )
              ) : null}
              {lookup?.employee ? (
                <div className="sla-found">
                  <h4>Link Information</h4>
                  <p className="sla-found__label">Linked By</p>
                  <LinkedByCell linkedBy={lookup.employee} />
                </div>
              ) : null}
              {linkError ? <p className="error-text">{linkError}</p> : null}
              <div className="sla-create-actions">
                <button
                  type="button"
                  className="secondary-page-btn"
                  onClick={() => setCreateOpen(false)}
                  disabled={linkSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary-page-btn"
                  disabled={
                    linkSaving ||
                    searching ||
                    availableLoading ||
                    !lookup?.employee?.id ||
                    !selectedAgencyId
                  }
                >
                  {linkSaving ? "Creating…" : "Create Link"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </section>
  );
}
